import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Help = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleContactFormChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError('');

    try {
      await axios.post('/api/contact', contactForm);
      setContactSuccess(true);
      setContactForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => {
        setShowContactForm(false);
        setContactSuccess(false);
      }, 3000);
    } catch (error) {
      setContactError(error.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "What is ObitAuthor?",
          answer: "ObitAuthor is a professional obituary generation service that helps you create beautiful, dignified obituaries for your loved ones. Our service provides instant generation, professional formatting, and print-ready PDFs."
        },
        {
          question: "How does it work?",
          answer: "Simply purchase our package for $9.99, receive 5 redemption codes via email, enter a code when you're ready, fill out a simple form about your loved one, and instantly receive a professionally formatted obituary with a downloadable PDF."
        },
        {
          question: "How much does it cost?",
          answer: "ObitAuthor costs $9.99 for a package of 5 fully generated obituaries. There are no hidden fees, no subscriptions, and no expiration dates. Use your codes whenever you need them."
        }
      ]
    },
    {
      category: "Redemption Codes",
      questions: [
        {
          question: "How do I receive my redemption codes?",
          answer: "After your purchase is complete, you'll receive an email at the address you provided during checkout. This email will contain your 5 unique redemption codes in the format OBIT-XXXX-XXXX-XXXX."
        },
        {
          question: "Do my codes expire?",
          answer: "No, your redemption codes never expire. You can use them whenever you need them, whether that's immediately or years from now."
        },
        {
          question: "Can I use one code multiple times?",
          answer: "No, each code is single-use only. Once you've used a code to generate an obituary, that code cannot be used again. This is why we provide 5 codes per package."
        },
        {
          question: "What if I lose my codes?",
          answer: "Your codes are sent to your email, so please save that email for your records. If you've lost your codes, please use the contact form at the bottom of this page with your purchase information and we'll help you recover them."
        },
        {
          question: "Can I share my codes with others?",
          answer: "Yes! You can share your unused codes with family members or friends who may need to create obituaries. Each code works independently."
        }
      ]
    },
    {
      category: "Creating Obituaries",
      questions: [
        {
          question: "What information do I need to provide?",
          answer: "Our form asks for basic information including: full name, birth and death dates, location, education, career, hobbies, achievements, family members (survived by/preceded by), and service information. Most fields are optional, allowing you to provide as much or as little detail as you prefer."
        },
        {
          question: "How long does it take to generate an obituary?",
          answer: "Obituaries are generated instantly! Once you submit the form, your professionally formatted obituary and PDF are created immediately and ready for download."
        },
        {
          question: "Can I edit an obituary after it's generated?",
          answer: "Currently, you cannot edit an obituary once it's generated. However, if you're not satisfied with the result, you can use another redemption code to create a new one with updated information."
        },
        {
          question: "What format is the obituary in?",
          answer: "Your obituary is provided in two formats: a web-based view you can read immediately, and a professional PDF file formatted for printing and sharing."
        }
      ]
    },
    {
      category: "PDF & Access",
      questions: [
        {
          question: "How do I download my PDF?",
          answer: "After your obituary is generated, you'll see a 'Download PDF' button on the results page. Click it to download your professionally formatted obituary PDF."
        },
        {
          question: "How long can I access my obituary?",
          answer: "Your obituary and PDF remain accessible for 90 days after generation. During this time, you can return to view or download it as many times as needed."
        },
        {
          question: "What happens after 90 days?",
          answer: "After 90 days, the PDF file is automatically removed from our servers for privacy and storage management. However, any PDFs you've downloaded during that time are yours to keep forever."
        },
        {
          question: "Can I print the obituary?",
          answer: "Absolutely! The PDF is specifically formatted for printing on standard letter-size paper. You can print it at home or take the PDF to a professional printer."
        },
        {
          question: "Is the ObitAuthor branding on the PDF?",
          answer: "Yes, there is minimal branding at the header and footer of the PDF showing 'ObitAuthor.com' and 'A Lavation Company'. This is subtle and professional, not detracting from the content."
        }
      ]
    },
    {
      category: "Payment & Security",
      questions: [
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit and debit cards through our secure payment processor, Stripe. Your payment information is never stored on our servers."
        },
        {
          question: "Is my payment information secure?",
          answer: "Yes, absolutely. All payments are processed through Stripe, one of the world's most trusted payment processors. We never see or store your payment details."
        },
        {
          question: "Will I receive a receipt?",
          answer: "Yes, you'll receive a payment confirmation and receipt via email immediately after your purchase."
        },
        {
          question: "What is your refund policy?",
          answer: "Due to the digital nature of our product (redemption codes sent immediately), all sales are final. However, if you experience technical issues or problems with the service, please use the contact form at the bottom of this page and we'll work with you to resolve the issue."
        }
      ]
    },
    {
      category: "Privacy & Data",
      questions: [
        {
          question: "What do you do with my information?",
          answer: "We take privacy seriously. Your personal information and the obituary details you provide are used only to generate your obituary. We do not sell, share, or use your data for any other purpose."
        },
        {
          question: "How is my data stored?",
          answer: "Your data is stored securely in our encrypted database. PDF files are stored for 90 days and then automatically deleted. Your redemption codes and basic purchase information are retained for record-keeping purposes."
        },
        {
          question: "Can I request deletion of my data?",
          answer: "Yes, you can request deletion of your data using the contact form at the bottom of this page. Note that this will also invalidate any unused redemption codes associated with your purchase."
        }
      ]
    },
    {
      category: "Technical Issues",
      questions: [
        {
          question: "My code isn't working. What should I do?",
          answer: "First, verify you're entering the code exactly as shown in your email (format: OBIT-XXXX-XXXX-XXXX). Codes are case-sensitive. If it still doesn't work, check if the code has already been used. If you continue to have issues, use the contact form at the bottom of this page for assistance."
        },
        {
          question: "I'm locked out after too many attempts. How long do I wait?",
          answer: "For security purposes, after 5 failed code validation attempts, access is temporarily restricted for 1 hour. This prevents automated abuse. After the hour expires, you can try again."
        },
        {
          question: "The PDF won't download. What can I do?",
          answer: "Try these steps: 1) Refresh the page and try again, 2) Try a different web browser, 3) Check if your browser is blocking downloads, 4) Ensure you have enough storage space. If problems persist, use the contact form at the bottom of this page."
        },
        {
          question: "I didn't receive my codes email. Where is it?",
          answer: "Check your spam/junk folder first, as automated emails sometimes end up there. Also verify you entered the correct email address during checkout. If you still can't find it after 15 minutes, use the contact form at the bottom of this page with your purchase details."
        },
        {
          question: "The website isn't loading properly. What should I do?",
          answer: "Try these steps: 1) Hard refresh your browser (Ctrl+Shift+R or Cmd+Shift+R), 2) Clear your browser cache, 3) Try a different browser, 4) Check your internet connection. If issues persist, use the contact form at the bottom of this page."
        }
      ]
    },
    {
      category: "Contact & Support",
      questions: [
        {
          question: "How can I contact support?",
          answer: "For any questions, issues, or concerns, please use the contact form at the bottom of this page. We typically respond within 24 hours during business days."
        },
        {
          question: "Do you offer phone support?",
          answer: "At this time, we provide support exclusively via email to ensure we can properly document and address your concerns."
        },
        {
          question: "Can you help me write an obituary?",
          answer: "While we provide the platform to generate professionally formatted obituaries, the content comes from the information you provide in the form. We recommend gathering information about your loved one's life, achievements, and relationships before starting."
        }
      ]
    },
    {
      category: "Pricing & Value",
      questions: [
        {
          question: "How much does an obituary cost at a funeral home?",
          answer: "Funeral homes typically charge $200-$500 or more to write an obituary. ObitAuthor provides the same professional quality for just $9.99, saving you $190-$490 or more."
        },
        {
          question: "Is $9.99 really for 5 obituaries?",
          answer: "Yes! For just $9.99, you receive 5 redemption codes, each good for one fully generated obituary. That's less than $2 per obituary compared to $200-$500 per obituary at funeral homes."
        },
        {
          question: "Why is ObitAuthor so much cheaper than funeral homes?",
          answer: "We use technology to streamline the process, eliminating overhead costs associated with traditional funeral homes. This allows us to offer professional quality at a fraction of the cost while maintaining dignity and respect."
        },
        {
          question: "Are there any hidden fees?",
          answer: "No hidden fees whatsoever. The $9.99 price includes everything: 5 obituary generations, professional formatting, PDF downloads, and 90 days of access. No subscriptions, no expiration dates, no additional charges."
        },
        {
          question: "What is your satisfaction guarantee?",
          answer: "We stand behind our work. If you're not satisfied with your obituary, we'll redo it for free! Simply contact us and we'll work with you to create an obituary that honors your loved one perfectly."
        }
      ]
    },
    {
      category: "SEO & Common Questions",
      questions: [
        {
          question: "How do I write an obituary?",
          answer: "ObitAuthor makes writing an obituary simple. After purchasing, you'll receive redemption codes. Enter a code, fill out our guided form with information about your loved one, and we'll instantly generate a professional obituary. No writing experience needed!"
        },
        {
          question: "What should I include in an obituary?",
          answer: "Essential information includes: full name, dates of birth and death, age, location, education, career, family members (survived by/preceded by), hobbies, achievements, and funeral service information. Our form guides you through all these sections."
        },
        {
          question: "How long should an obituary be?",
          answer: "Most obituaries are 200-500 words, though length can vary. ObitAuthor generates obituaries of appropriate length based on the information you provide. Newspapers often charge by length, so we help you create meaningful content that's also cost-effective."
        },
        {
          question: "Can I write my own obituary?",
          answer: "Absolutely! Many people write their own obituaries in advance. ObitAuthor can help you create a professional format, or you can use our service when the time comes. Our form makes it easy whether you're writing for yourself or a loved one."
        },
        {
          question: "What's the difference between an obituary and a death notice?",
          answer: "A death notice is brief and factual (name, date of death, service info). An obituary tells the story of a person's life, including achievements, relationships, and impact. ObitAuthor helps you create full obituaries that honor your loved one's memory."
        },
        {
          question: "How do I submit an obituary to a newspaper?",
          answer: "After generating your obituary with ObitAuthor, you can copy the text or use the PDF. Contact your local newspaper's obituary department for submission guidelines, deadlines, and pricing. Most newspapers accept submissions via email or their website."
        },
        {
          question: "What is an obituary template?",
          answer: "An obituary template is a structured format that helps organize information. ObitAuthor uses professional templates to ensure your obituary includes all important elements while maintaining proper formatting and flow."
        },
        {
          question: "How much does it cost to publish an obituary in a newspaper?",
          answer: "Newspaper publication costs vary widely ($50-$500+ depending on length and publication). ObitAuthor helps you create the obituary text, but you'll need to pay the newspaper separately for publication. Many families save money by publishing shorter versions or using online-only options."
        }
      ]
    }
  ];

  // Filter FAQs based on search term
  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(faq => 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  // Remove Service schema from /help page (it should only be on homepage)
  useEffect(() => {
    // Find and remove Service schema scripts
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent);
        if (data['@type'] === 'Service') {
          script.remove();
        }
      } catch (e) {
        // Ignore parsing errors
      }
    });
  }, []);

  // Add FAQPage structured data
  useEffect(() => {
    // Flatten all FAQs from all categories
    const allFaqs = faqs.flatMap(category => category.questions);
    
    const faqPageSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": allFaqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    // Remove ALL existing FAQPage structured data scripts
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent);
        if (data['@type'] === 'FAQPage') {
          script.remove();
        }
      } catch (e) {
        // Ignore parsing errors
      }
    });

    // Add new FAQPage structured data
    const script = document.createElement('script');
    script.id = 'faqpage-structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqPageSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const scriptToRemove = document.getElementById('faqpage-structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []); // Empty dependency array - only run once on mount

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="glass-card text-center mb-4" style={{ position: 'relative', zIndex: 20 }}>
            <h1 className="commercial-heading mb-3">Help & FAQ</h1>
            <p className="commercial-subheading mb-4">
              Find answers to common questions about ObitAuthor
            </p>
            
            {/* Search Box */}
            <div className="search-box mx-auto" style={{ maxWidth: '600px' }}>
              <div className="input-group input-group-lg">
                <span className="input-group-text" style={{ 
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#1e40af'
                }}>
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search for answers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: '#000000'
                  }}
                />
                {searchTerm && (
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchTerm('')}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: '#1e40af'
                    }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
              {searchTerm && (
                <p className="mt-2 mb-0" style={{ color: '#000000', fontSize: '0.9rem' }}>
                  Found {filteredFaqs.reduce((sum, cat) => sum + cat.questions.length, 0)} result(s)
                </p>
              )}
            </div>
          </div>

          {searchTerm && filteredFaqs.length === 0 && (
            <div className="glass-card text-center mb-5" style={{ position: 'relative', zIndex: 20 }}>
              <i className="bi bi-search" style={{ fontSize: '3rem', color: '#6b7280' }}></i>
              <h3 style={{ color: '#000000', marginTop: '1rem' }}>No results found</h3>
              <p style={{ color: '#000000' }}>Try different keywords or browse all questions below</p>
              <button className="btn btn-primary" onClick={() => setSearchTerm('')}>
                Clear Search
              </button>
            </div>
          )}

          {filteredFaqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-5" style={{ position: 'relative', zIndex: 20 }}>
              <h2 className="category-heading mb-4" style={{ 
                color: '#000000', 
                fontSize: '1.75rem',
                textShadow: '0 0 12px rgba(255, 255, 255, 0.9), 0 0 24px rgba(255, 255, 255, 0.7)',
                fontWeight: '600',
                position: 'relative',
                zIndex: 20
              }}>
                {category.category}
              </h2>

              <div className="accordion-container">
                {category.questions.map((faq, questionIndex) => {
                  const globalIndex = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openIndex === globalIndex;

                  return (
                    <div 
                      key={questionIndex} 
                      className="faq-item faq-accordion-card glass-card mb-3"
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: isOpen ? '2px solid rgba(59, 130, 246, 0.6)' : '1px solid rgba(255, 255, 255, 0.3)',
                        padding: '0'
                      }}
                      onClick={() => toggleAccordion(globalIndex)}
                    >
                      <div 
                        className="faq-question d-flex justify-content-between align-items-center"
                        style={{ padding: '1rem 1.5rem' }}
                      >
                        <h3 style={{ 
                          margin: 0, 
                          fontSize: '1.1rem', 
                          fontWeight: '600',
                          color: '#000000',
                          flex: 1,
                          textShadow: '0 0 8px rgba(255, 255, 255, 0.8)'
                        }}>
                          {faq.question}
                        </h3>
                        <i 
                          className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}
                          style={{ 
                            fontSize: '1.5rem', 
                            color: '#3b82f6',
                            transition: 'transform 0.3s ease',
                            marginLeft: '1rem'
                          }}
                        ></i>
                      </div>
                      
                      {isOpen && (
                        <div 
                          className="faq-answer"
                          style={{
                            padding: '0 1.5rem 1rem 1.5rem',
                            borderTop: '1px solid rgba(59, 130, 246, 0.3)',
                            marginTop: '0.5rem',
                            paddingTop: '0.75rem',
                            animation: 'fadeIn 0.3s ease-in'
                          }}
                        >
                          <p style={{ 
                            margin: 0, 
                            lineHeight: '1.8',
                            color: '#000000',
                            fontSize: '1rem'
                          }}>
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="glass-card text-center mt-5" style={{ background: 'rgba(59, 130, 246, 0.15)', position: 'relative', zIndex: 20 }}>
            <h3 style={{ 
              color: '#000000',
              textShadow: '0 0 12px rgba(255, 255, 255, 0.9)',
              marginBottom: '1rem'
            }}>
              Still Have Questions?
            </h3>
            <p style={{ color: '#000000', marginBottom: '1.5rem' }}>
              We're here to help! Contact our support team for personalized assistance.
            </p>
            
            {!showContactForm ? (
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setShowContactForm(true)}
              >
                <i className="bi bi-envelope me-2"></i>
                Contact Support
              </button>
            ) : (
              <div className="contact-form-container mt-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
                {contactSuccess ? (
                  <div className="alert alert-success" role="alert">
                    <i className="bi bi-check-circle me-2"></i>
                    Message sent successfully! We'll get back to you soon.
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="text-start">
                    <div className="mb-3">
                      <label className="form-label">Your Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactFormChange}
                        required
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={contactForm.email}
                        onChange={handleContactFormChange}
                        required
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Subject *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="subject"
                        value={contactForm.subject}
                        onChange={handleContactFormChange}
                        required
                        placeholder="Brief description of your issue"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Message *</label>
                      <textarea
                        className="form-control"
                        name="message"
                        rows="5"
                        value={contactForm.message}
                        onChange={handleContactFormChange}
                        required
                        placeholder="Please provide details about your question or issue..."
                      />
                    </div>
                    
                    {contactError && (
                      <div className="alert alert-danger" role="alert">
                        {contactError}
                      </div>
                    )}
                    
                    <div className="d-flex gap-3">
                      <button
                        type="button"
                        className="btn btn-secondary flex-fill"
                        onClick={() => setShowContactForm(false)}
                        disabled={contactSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary flex-fill"
                        disabled={contactSubmitting}
                      >
                        {contactSubmitting ? 'Sending...' : 'Send Message'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Help;

